import React from 'react';
import './AppButton.css';

export type AppButtonVariant =
    | 'primary'
    | 'secondary'
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-danger'
    | 'outline-dark'
    | 'ghost'
    | 'app-default'
    | 'custom';

export type AppButtonSize = 'sm' | 'md' | 'lg';
export type AppButtonWeight = 'normal' | 'semibold' | 'bold';
export type AppButtonRadius = 'md' | 'pill' | 'none';

type AppButtonBaseProps = {
    variant?: AppButtonVariant;
    size?: AppButtonSize;
    weight?: AppButtonWeight;
    radius?: AppButtonRadius;
    px?: number;
    py?: number;
    fullWidth?: boolean;
    className?: string;
};

export type AppButtonProps = AppButtonBaseProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

const variantClassName: Record<AppButtonVariant, string> = {
    primary: 'btn btn-primary',
    secondary: 'btn btn-secondary',
    'outline-primary': 'btn btn-outline-primary',
    'outline-secondary': 'btn btn-outline-secondary',
    'outline-danger': 'btn btn-outline-danger',
    'outline-dark': 'btn btn-outline-dark',
    ghost: 'btn p-0 border-0 bg-transparent',
    'app-default': 'app-btn app-btn-default',
    custom: '',
};

const weightClassName: Record<AppButtonWeight, string> = {
    normal: 'fw-normal',
    semibold: 'fw-semibold',
    bold: 'fw-bold',
};

const radiusClassName: Record<AppButtonRadius, string> = {
    md: 'rounded-3',
    pill: 'rounded-pill',
    none: 'rounded-0',
};

const withClass = (...classes: Array<string | undefined | false | null>) => {
    return classes.filter(Boolean).join(' ');
};

const sizeClassForVariant = (variant: AppButtonVariant, size: AppButtonSize): string | undefined => {
    if (size === 'md') {
        return undefined;
    }
    if (variant === 'app-default') {
        return size === 'sm' ? 'app-btn-sm' : 'app-btn-lg';
    }
    return size === 'sm' ? 'btn-sm' : 'btn-lg';
};

export const getAppButtonClassName = ({
    variant = 'primary',
    size = 'md',
    weight,
    radius,
    px,
    py,
    fullWidth,
    className,
}: AppButtonBaseProps) => {
    return withClass(
        'app-ui-button',
        variantClassName[variant],
        sizeClassForVariant(variant, size),
        weight ? weightClassName[weight] : undefined,
        radius ? radiusClassName[radius] : undefined,
        typeof px === 'number' ? `px-${px}` : undefined,
        typeof py === 'number' ? `py-${py}` : undefined,
        fullWidth ? 'w-100' : undefined,
        className
    );
};

const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>((props, ref) => {
    const buttonClassName = getAppButtonClassName({
        variant: props.variant ?? 'primary',
        size: props.size ?? 'md',
        weight: props.weight,
        radius: props.radius,
        px: props.px,
        py: props.py,
        fullWidth: props.fullWidth,
        className: props.className,
    });

    const {
        variant: _variant,
        size: _size,
        weight: _weight,
        radius: _radius,
        px: _px,
        py: _py,
        fullWidth: _fullWidth,
        className: _className,
        ...buttonProps
    } = props;
    return <button ref={ref} className={buttonClassName} {...buttonProps} />;
});

AppButton.displayName = 'AppButton';

export default AppButton;
